import React, { useState, useEffect, useCallback } from 'react';
import {
  DropdownMenu,
  Modal,
  Toast,
  useStateContext,
} from '@veda/dashboard-ui';
import {
  MdCheckCircle, MdCancel, MdPerson, MdEmail, MdBadge, MdAccountBalance,
} from 'react-icons/md';
import { usePppApi } from '../../ppp/services/pppApi';

const STATI = [
  { key: 'pending',   label: 'In attesa' },
  { key: 'approvata', label: 'Approvate' },
  { key: 'rifiutata', label: 'Rifiutate' },
];

function Badge({ stato }) {
  const map = {
    pending:   ['amber', 'In attesa'],
    approvata: ['green', 'Approvata'],
    rifiutata: ['gray',  'Rifiutata'],
  };
  const colors = {
    green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    gray:  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  };
  const [color, label] = map[stato] ?? ['gray', stato];
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[color]}`}>{label}</span>;
}

export default function RichiesteAccessoRupList() {
  const api = usePppApi();
  const { addToast, addModal } = useStateContext();

  const [filtro,    setFiltro]    = useState('pending');
  const [filtroTipo, setFiltroTipo] = useState('');   // '' = tutti, 'rup', 'ente'
  const [richieste, setRichieste] = useState([]);
  const [loading,   setLoading]   = useState(true);

  // Modale rifiuto: la nota vive nella pagina perché il Modal Veda chiama
  // onClick senza passare alcuno stato interno.
  const [rifiutando,  setRifiutando]  = useState(null); // richiesta in corso di rifiuto
  const [noteRifiuto, setNoteRifiuto] = useState('');

  const carica = useCallback(() => {
    setLoading(true);
    api.listRichiesteRup(filtro, filtroTipo || null, {
      onSuccess: (res) => { setRichieste(res?.data ?? []); setLoading(false); },
      onError:   () => setLoading(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro, filtroTipo]);

  useEffect(() => { carica(); }, [carica]);

  const handleApprova = (r) => {
    const content =
      r.tipo === 'ente'
        ? `Verrà creato (o riusato) l'ente "${r.enteDenominazione}" e un account Amministratore Ente collegato. L'utente potrà accedere con SPID e riceverà una email di conferma. Procedere?`
      : r.tipo === 'company'
        ? `Verrà creata (o riusata) l'azienda "${r.companyRagioneSociale}" e un account Operatore Economico collegato. L'utente potrà accedere con SPID e riceverà una email di conferma. Procedere?`
        : "Verrà creato un account RUP. L'utente potrà accedere con SPID e riceverà una email di conferma. Procedere?";
    const toastMsg =
      r.tipo === 'ente'    ? "Richiesta approvata: ente e amministratore creati."
    : r.tipo === 'company' ? "Richiesta approvata: azienda e operatore creati."
    :                        "Richiesta approvata: utente RUP creato.";
    addModal(
      <Modal
        title={`Approva ${r.name}`}
        content={content}
        confirm="Approva"
        style="success"
        onClick={() => {
          api.approvaRichiestaRup(r.id, {
            onSuccess: () => {
              addToast(<Toast duration={4} content={toastMsg} />);
              carica();
            },
            onFormError: () => addToast(<Toast duration={4} content="Errore durante l'approvazione." />),
          });
        }}
      />
    );
  };

  const apriRifiuto = (r) => {
    setRifiutando(r);
    setNoteRifiuto('');
  };

  const confermaRifiuto = () => {
    if (!rifiutando || noteRifiuto.trim().length < 5) return;
    const r = rifiutando;
    api.rifiutaRichiestaRup(r.id, { note_rifiuto: noteRifiuto.trim() }, {
      onSuccess: () => {
        setRifiutando(null);
        addToast(<Toast duration={4} content="Richiesta rifiutata." />);
        carica();
      },
      onFormError: () => addToast(<Toast duration={4} content="Errore durante il rifiuto." />),
    });
  };

  const menuItems = (r) => [
    <button
      key="approva"
      onClick={() => handleApprova(r)}
      className="w-full flex items-center gap-4 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
    >
      <MdCheckCircle size={16} className="text-green-600" />
      <span>Approva</span>
    </button>,
    <button
      key="rifiuta"
      onClick={() => apriRifiuto(r)}
      className="w-full flex items-center gap-4 px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600"
    >
      <MdCancel size={16} />
      <span>Rifiuta</span>
    </button>,
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300">
          <MdAccountBalance size={24} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Richieste di accesso</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Vaglia le richieste di accesso inoltrate dagli aspiranti RUP e dagli enti.
          </p>
        </div>
      </div>

      {/* Filtri stato */}
      <div className="flex gap-2 mb-3">
        {STATI.map((s) => (
          <button
            key={s.key}
            onClick={() => setFiltro(s.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filtro === s.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Filtri tipo */}
      <div className="flex gap-2 mb-4">
        {[
          { key: '', label: 'Tutti i tipi' },
          { key: 'rup', label: 'RUP' },
          { key: 'ente', label: 'Ente' },
          { key: 'company', label: 'Operatore Economico' },
        ].map((t) => (
          <button
            key={t.key || 'all'}
            onClick={() => setFiltroTipo(t.key)}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              filtroTipo === t.key
                ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 py-12 text-center">Caricamento...</p>
      ) : richieste.length === 0 ? (
        <div className="py-16 text-center text-gray-400">
          <MdPerson size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Nessuna richiesta {STATI.find((s) => s.key === filtro)?.label.toLowerCase()}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {richieste.map((r) => (
            <div
              key={r.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 flex items-start justify-between gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-800 dark:text-white">{r.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    r.tipo === 'ente'
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                      : r.tipo === 'company'
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  }`}>
                    {r.tipo === 'ente' ? 'Ente' : r.tipo === 'company' ? 'Operatore Economico' : 'RUP'}
                  </span>
                  <Badge stato={r.stato} />
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                  <span className="inline-flex items-center gap-1"><MdEmail size={14} /> {r.email}</span>
                  {r.codiceFiscale && <span className="inline-flex items-center gap-1"><MdBadge size={14} /> {r.codiceFiscale}</span>}
                </div>

                {r.tipo === 'ente' && (
                  <div className="mt-2 rounded-lg bg-purple-50/60 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30 px-3 py-2 text-sm">
                    <p className="font-medium text-gray-800 dark:text-white">{r.enteDenominazione}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {r.enteTipologia && <span>{r.enteTipologia}</span>}
                      {r.enteCodiceIpa && <span>IPA: {r.enteCodiceIpa}</span>}
                      {r.entePec && <span>{r.entePec}</span>}
                    </div>
                  </div>
                )}

                {r.tipo === 'company' && (
                  <div className="mt-2 rounded-lg bg-emerald-50/60 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 px-3 py-2 text-sm">
                    <p className="font-medium text-gray-800 dark:text-white">{r.companyRagioneSociale}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {r.companyPartitaIva && <span>P.IVA: {r.companyPartitaIva}</span>}
                      {r.companyCodiceFiscale && <span>CF: {r.companyCodiceFiscale}</span>}
                      {r.companyPec && <span>{r.companyPec}</span>}
                    </div>
                  </div>
                )}

                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 whitespace-pre-wrap">{r.motivazione}</p>
                {r.stato === 'rifiutata' && r.noteRifiuto && (
                  <p className="text-xs text-red-500 mt-2"><strong>Motivo rifiuto:</strong> {r.noteRifiuto}</p>
                )}
                {r.valutataAt && (
                  <p className="text-xs text-gray-400 mt-2">
                    Valutata da {r.valutataDa ?? '—'} il {new Date(r.valutataAt).toLocaleDateString('it-IT')}
                  </p>
                )}
              </div>

              {r.stato === 'pending' && (
                <DropdownMenu menu_items={menuItems(r)} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pannello rifiuto con motivazione */}
      {rifiutando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg w-full max-w-md p-6">
            <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-3">
              Rifiuta richiesta di {rifiutando.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Indica una motivazione: verrà inviata via email al richiedente.
            </p>
            <textarea
              rows={4}
              value={noteRifiuto}
              onChange={(e) => setNoteRifiuto(e.target.value)}
              placeholder="Motivazione del rifiuto..."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setRifiutando(null)}
                className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Annulla
              </button>
              <button
                onClick={confermaRifiuto}
                disabled={noteRifiuto.trim().length < 5}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white"
              >
                Rifiuta richiesta
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
